// Tutor AttendancePage — load a session's attendance and check students in.
import { useState } from "react";
import type { FormEvent } from "react";
import { useFetch, postJson } from "../../hooks/useApi.js";
import Badge from "../../components/shared/Badge.js";
import type { AttendanceRecord } from "@dm-lms/shared/types";

export default function AttendancePage() {
  const [sessionId, setSessionId] = useState("");
  const [loadedSessionId, setLoadedSessionId] = useState<string | null>(null);
  const [studentId, setStudentId] = useState("");
  const [checkInError, setCheckInError] = useState<string | null>(null);
  const [checkInSuccess, setCheckInSuccess] = useState(false);

  const { data: attendance, isLoading, error, refetch } = useFetch<AttendanceRecord[]>(
    loadedSessionId ? `/api/attendance/session/${loadedSessionId}` : null
  );

  async function handleCheckIn(e: FormEvent) {
    e.preventDefault();
    setCheckInError(null);
    setCheckInSuccess(false);
    if (!sessionId || !studentId) return;

    const result = await postJson<null>("/api/attendance/check-in", { studentId, sessionId });
    if (!result.ok) {
      setCheckInError(result.error);
      return;
    }
    setCheckInSuccess(true);
    setStudentId("");
    if (sessionId === loadedSessionId) refetch();
  }

  return (
    <div className="dm-page">
      <h1 className="text-2xl font-semibold text-dm-navy mb-6">Attendance</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <h2 className="text-sm font-medium text-dm-navy mb-3">View session attendance</h2>
          <label className="block text-xs text-dm-slate mb-1">Session ID</label>
          <input
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm mb-3"
            value={sessionId}
            onChange={(e) => setSessionId(e.target.value)}
            placeholder="Session ID"
          />
          <button
            type="button"
            className="bg-dm-blue text-white text-sm px-4 py-2 rounded-md disabled:opacity-50"
            disabled={!sessionId}
            onClick={() => setLoadedSessionId(sessionId)}
          >
            Load Attendance
          </button>
        </div>

        <form className="rounded-lg border border-gray-200 bg-white p-4" onSubmit={handleCheckIn}>
          <h2 className="text-sm font-medium text-dm-navy mb-3">Check in a student</h2>
          <label className="block text-xs text-dm-slate mb-1">Student ID</label>
          <input
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm mb-3"
            value={studentId}
            onChange={(e) => setStudentId(e.target.value)}
            placeholder="Student ID"
          />
          <p className="text-xs text-dm-slate mb-3">Uses the Session ID entered on the left.</p>
          <button
            type="submit"
            className="bg-dm-gold text-dm-navy text-sm px-4 py-2 rounded-md font-medium disabled:opacity-50"
            disabled={!sessionId || !studentId}
          >
            Check In
          </button>
          {checkInError && <p className="text-red-600 text-xs mt-2">{checkInError}</p>}
          {checkInSuccess && <p className="text-green-700 text-xs mt-2">Checked in successfully.</p>}
        </form>
      </div>

      {loadedSessionId && (
        <>
          <h2 className="text-lg font-medium text-dm-navy mb-3">Attendance for session {loadedSessionId}</h2>
          {isLoading && <p className="text-dm-slate text-sm">Loading…</p>}
          {error && <p className="text-red-600 text-sm">{error}</p>}
          {!isLoading && !error && (
            (attendance ?? []).length === 0 ? (
              <p className="text-dm-slate text-sm">No attendance records for this session yet.</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left text-dm-slate">
                    <tr>
                      <th className="px-4 py-3 font-medium">Student ID</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium">Checked in</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(attendance ?? []).map((record) => (
                      <tr key={record.id}>
                        <td className="px-4 py-3">{record.studentId}</td>
                        <td className="px-4 py-3">
                          <Badge tone={record.status === "present" ? "green" : record.status === "excused" ? "gold" : "red"}>
                            {record.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          {record.checkedInAt ? new Date(record.checkedInAt).toLocaleString() : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </>
      )}
    </div>
  );
}
